import React from 'react';
import { NameEmailAvatar } from '../../../../../../ui';
import { AssessmentUserApiResponse } from '../Interfaces';

import { TableColumn } from '../Interfaces/TableColumn';

export const TooltipWrapper: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div className='relative group w-full flex items-center justify-start text-left'>
      { /* Truncated Text */ }
      <div className='max-w-[180px] truncate whitespace-nowrap overflow-hidden'>
        { text || '-' }
      </div>

      { /* Tooltip */ }
      <div className='absolute z-[9999] hidden group-hover:flex flex-col items-center'>
        <div className='relative'>
          { /* Tooltip Box */ }
          <div className='absolute bottom-full px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg w-max max-w-xs whitespace-normal'>
            { text }
          </div>
        </div>
      </div>
    </div>
  );
};

// Format time from seconds to HH:MM:SS or MM:SS format
const formatTime = (seconds: number | undefined): JSX.Element => {
  if (!seconds || seconds === 0) return <span>00:00</span>;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return (
      <span>{ `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds
        .toString()
        .padStart(2, '0')}` }</span>
    );
  }
  return (
    <span>{ `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}` }</span>
  );
};

// Format timestamp to readable date
const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Calculate skill percentage
const calculateSkillPercentage = (skill: any): number => {
  if (!skill || skill.totalCount === 0) return 0;
  return Math.round((skill.correctCount / skill.totalCount) * 100);
};

// Get candidate status badge color
export const getStatusColor = (status: string | undefined): string => {
  if (!status) return 'bg-gray-500';

  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-500';
    case 'shortlisted':
      return 'bg-blue-500';
    case 'waitlisted':
      return 'bg-yellow-500';
    case 'unsuccessful':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

// Generate columns based on assessment content
export const getColumns = (hasSkillQuestions: boolean = true, hasBEIQuestions: boolean = true): TableColumn<AssessmentUserApiResponse>[] => {
  const columns: TableColumn<AssessmentUserApiResponse>[] = [];

  // 1. Candidate column (always first)
  columns.push({
    key: '_id',
    label: 'Candidate',
    width: '220px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <NameEmailAvatar
          name={`${row.user.firstName} ${row.user.lastName}`}
          email={row.user.email}
          avatar=''
      />
    ),
    disableSort: false,
  });

  // 2. Skills Score (if has skill questions)
  if (hasSkillQuestions) {
    columns.push({
      key: 'score',
      label: 'Skills Score',
      width: '120px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => (
        <div className='flex flex-col items-start'>
          <div className='text-gray-500'>{ row.score }</div>
        </div>
      ),
      disableSort: false,
    });
  }

  // 3. Skills % (if has skill questions)
  if (hasSkillQuestions) {
    columns.push({
      key: 'score',
      label: 'Skills %',
      width: '100px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => {
        const percentage =
          row.maxScore > 0 ? Math.round((row.score / row.maxScore) * 100) : 0;
        return (
          <div className='flex flex-col items-start'>
            <div className='text-sm text-gray-600'>{ percentage }%</div>
          </div>
        );
      },
      disableSort: false,
    });
  }

  // 4. Behaviour Score (if has BEI questions)
  if (hasBEIQuestions) {
    columns.push({
      key: 'behavior_score',
      label: 'Behaviour %',
      width: '150px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => (
        <div className='flex flex-col items-start'>
          <div className='text-sm text-gray-600'>{ row.behaviorScore || 0 }%</div>
        </div>
      ),
      disableSort: false,
    });
  }

  // Note: Dynamic skill columns will be inserted here by the caller

  // 5. Time Taken (if has skill questions)
  if (hasSkillQuestions) {
    columns.push({
      key: 'total_time',
      label: 'Time Taken',
      width: '120px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => (
        <div className='flex flex-col items-start'>
          <div className='font-medium'>{ formatTime(row.totalTime) }</div>
          <div className='text-xs text-gray-500'>Total</div>
        </div>
      ),
      disableSort: false,
    });
  }

  // 6. Start Date
  columns.push({
    key: 'startDate',
    label: 'Start Date',
    width: '100px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <div className='text-xs font-medium'>{ formatDate(row.startTime) }</div>
    ),
    disableSort: false,
  });

  // 7. End Date
  columns.push({
    key: 'end_time',
    label: 'End Date',
    width: '100px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <div className='text-xs font-medium'>{ formatDate(row.endTime) }</div>
    ),
    disableSort: false,
  });

  // 8. Candidate Status
  columns.push({
    key: 'candidateStatus',
    label: 'Candidate Status',
    width: '120px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <div className='space-y-2'>
        { /* Candidate Status */ }
        <div className='flex items-center gap-2'>
          <div
              className={`w-2 h-2 rounded-full ${getStatusColor(
                row.candidateStatus,
              )}`}
          />
          <span className='text-sm font-medium capitalize'>
            { row.candidateStatus }
          </span>
        </div>
      </div>
    ),
    disableSort: true,
  });

  // 9. Assessment Status
  columns.push({
    key: 'status',
    label: 'Assessment Status',
    width: '140px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <div className='space-y-2'>
        { /* Candidate Status */ }
        <div className='flex items-center gap-2'>
          <div
              className={`w-2 h-2 rounded-full ${getStatusColor(row.status)}`}
          />
          <span className='text-sm font-medium capitalize'>{ row.status }</span>
        </div>
      </div>
    ),
    disableSort: true,
  });

  return columns;
};

